import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { UserPermissionsService } from '../services/user-permissions.service';
import { CognitoServiceService } from '../services/auth/cognito-service.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard {
  constructor(
    private userPermissionsService: UserPermissionsService,
    private router: Router,
    private authService: CognitoServiceService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {

    // Verificar si hay token válido (refrescar si es necesario)
    const validToken = await this.authService.getValidToken();
    if (!validToken) {
      console.warn('No hay token de autenticación válido');
      return this.router.createUrlTree(['/authentication/login']);
    }

    // Obtener el código del módulo desde la configuración de la ruta
    const moduleCode = route.data['moduleCode'] as string;

    // Si no se especifica moduleCode, permitir acceso (ruta sin restricción de permisos)
    if (!moduleCode) {
      return true;
    }

    // Obtener permisos actuales
    const permissions = this.userPermissionsService.getPermissions();

    // Si no hay permisos cargados, intentar cargarlos
    if (permissions.length === 0) {
      console.warn('Permisos no cargados, intentando cargar...');

      // Cargar permisos y verificar acceso
      return new Promise((resolve) => {
        this.userPermissionsService.loadUserPermissions().subscribe({
          next: () => {
            const hasAccess = this.userPermissionsService.canViewModule(moduleCode);
            if (hasAccess) {
              resolve(true);
            } else {
              console.warn(`Acceso denegado al módulo: ${moduleCode}`);
              resolve(this.router.createUrlTree(['/authentication/error'], {
                queryParams: { message: 'No tienes permisos para acceder a este módulo' }
              }));
            }
          },
          error: (error) => {
            console.error('Error cargando permisos:', error);
            resolve(this.router.createUrlTree(['/authentication/error'], {
              queryParams: { message: 'Error verificando permisos' }
            }));
          }
        });
      });
    }

    // Verificar si el usuario tiene permiso para ver el módulo
    const hasAccess = this.userPermissionsService.canViewModule(moduleCode);

    if (!hasAccess) {
      console.warn(`Acceso denegado al módulo: ${moduleCode}`);
      return this.router.createUrlTree(['/authentication/error'], {
        queryParams: { message: 'No tienes permisos para acceder a este módulo' }
      });
    }

    return true;
  }

  async canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }
}
